/**
 * Life Expectancy Data for Australians
 * Source: Australian Bureau of Statistics (ABS) Life Tables 2020-2022
 * Updated: October 2025
 */

export const lifeExpectancyData = {
  male: {
    55: { p50: 84.5, p25: 90.2, p10: 95.8, p5: 98.2 },
    60: { p50: 85.2, p25: 90.8, p10: 96.2, p5: 98.5 },
    65: { p50: 86.8, p25: 92.4, p10: 97.0, p5: 99.4 },
    70: { p50: 87.8, p25: 93.0, p10: 97.4, p5: 99.7 },
    75: { p50: 89.1, p25: 93.9, p10: 97.9, p5: 100.1 },
    80: { p50: 90.5, p25: 95.0, p10: 98.6, p5: 100.6 },
    85: { p50: 92.3, p25: 96.4, p10: 99.5, p5: 101.4 },
    90: { p50: 94.5, p25: 98.2, p10: 100.8, p5: 102.5 }
  },
  female: {
    55: { p50: 87.8, p25: 93.5, p10: 98.5, p5: 100.8 },
    60: { p50: 88.4, p25: 93.9, p10: 98.8, p5: 101.0 },
    65: { p50: 89.6, p25: 94.8, p10: 99.3, p5: 101.4 },
    70: { p50: 90.5, p25: 95.5, p10: 99.7, p5: 101.7 },
    75: { p50: 91.7, p25: 96.5, p10: 100.3, p5: 102.2 },
    80: { p50: 93.2, p25: 97.7, p10: 101.1, p5: 102.9 },
    85: { p50: 95.0, p25: 99.1, p10: 102.1, p5: 103.8 },
    90: { p50: 97.2, p25: 100.8, p10: 103.5, p5: 105.0 }
  }
};

/**
 * Get life expectancy percentiles for a given age and gender
 * @param {number} age - Current age
 * @param {string} gender - 'male' or 'female'
 * @returns {object} Percentile data {p50, p25, p10, p5}
 */
export const getLifeExpectancy = (age, gender = 'male') => {
  const genderData = lifeExpectancyData[gender];
  if (!genderData) {
    return { p50: 85, p25: 92, p10: 98, p5: 100 };
  }

  // Find nearest age in table
  const ages = Object.keys(genderData).map(Number).sort((a, b) => a - b);
  let closestAge = ages[0];
  let minDiff = Math.abs(age - closestAge);

  for (const tableAge of ages) {
    const diff = Math.abs(age - tableAge);
    if (diff < minDiff) {
      minDiff = diff;
      closestAge = tableAge;
    }
  }

  return genderData[closestAge] || { p50: 85, p25: 92, p10: 98, p5: 100 };
};

/**
 * Calculate years until life expectancy
 * @param {number} currentAge - Current age
 * @param {string} gender - 'male' or 'female'
 * @param {string} percentile - 'p50', 'p25', 'p10', or 'p5'
 * @returns {number} Years until life expectancy
 */
export const getYearsToLifeExpectancy = (currentAge, gender = 'male', percentile = 'p50') => {
  const data = getLifeExpectancy(currentAge, gender);
  const expectancyAge = data[percentile] || data.p50;
  return Math.max(0, Math.round(expectancyAge - currentAge));
};

/**
 * Get life expectancy description
 */
export const getLifeExpectancyDescription = (age, gender) => {
  const data = getLifeExpectancy(age, gender);
  return `Life expectancy: ${Math.round(data.p50)} years (50% probability), ${Math.round(data.p25)} years (25%), ${Math.round(data.p10)} years (10%)`;
};

export default {
  lifeExpectancyData,
  getLifeExpectancy,
  getYearsToLifeExpectancy,
  getLifeExpectancyDescription
};

